<template>
  <div>
    <page-header class="mt-6 lg:mt-0" :full-width="true">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full lg:w-1/2 px-2">
          <h1 slot="header" class="text-grey-darker text-xl font-bold leading-normal">Property Management</h1>
          <p class="text-grey-dark">Filter your property list below to access the property editor.</p>
        </div>

        <div class="w-full lg:w-1/2 px-2">
          <div v-if="!creatingProperty" class="justify-end flex flex-wrap">
            <button type="button"
                    class="bg-tertiary hover:border-tertiary text-white py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2 mb-2 md:w-1/4 md:mr-0"
                    @click="newProperty('RS')">
              <i class="fas fa-plus-circle"></i> Sales Property
            </button>

            <button type="button"
                    class="bg-tertiary hover:border-tertiary text-white py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mb-2 md:w-1/4 md:mr-0"
                    @click="newProperty('RL')">
              <i class="fas fa-plus-circle"></i> Rental Property
            </button>

            <button type="button"
                    class="bg-tertiary hover:border-primary text-white py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mb-2 md:w-1/3 md:mr-0"
                    @click="createMagazinePage()" v-if="!loading && selectedOffice.network_id === 2">
              <loader v-if="loadingDigitalHub" :fillColor="'white'"/>
              <span v-else>
                <i class="fas fa-file"></i> Create Magazine Page
              </span>
            </button>

            <button type="button"
                    class="bg-tertiary hover:border-primary text-white py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mb-2 md:w-1/4 md:mr-0"
                    @click="goToEhouse()" v-else-if="meta.ehouse_enabled && !loading">
              <loader v-if="loadingEhouse" :fillColor="'white'"/>
              <span v-else>
                <i class="fas fa-file"></i> Create Sales Particulars
              </span>
            </button>
          </div>
          <loader v-if="creatingProperty" class="mb-2"></loader>
        </div>
      </div>
    </page-header>

    <div class="w-full" v-if="!loading">
      <input type="text" v-model="searchParams.q" placeholder="Search"
             class="w-full mb-3 sm:w-full md:w-1/3 md:mb-0 lg:mb-0 xl:mb-0 p-4 mr-6 rounded"/>

      <div class="uppercase inline-block mr-2 md:mr-4 lg:mr-3 xl:mr-3 text-xs">
        <input type="radio" id="department_rs" v-model="searchParams.department" value="RS">
        <label for="department_rs">Sales</label>
      </div>

      <div class="uppercase inline-block mr-2 md:mr-4 lg:mr-3 xl:mr-3 text-xs">
        <input type="radio" id="department_rl" v-model="searchParams.department" value="RL">
        <label for="department_rl">Lettings</label>
      </div>

      <div class="uppercase inline-block mr-2 md:mr-4 lg:mr-3 xl:mr-3 text-xs">
        <input type="radio" id="active" v-model="searchParams.active" value="active">
        <label for="active">Active</label>
      </div>

      <div class="uppercase inline-block mr-2 md:mr-4 lg:mr-3 xl:mr-3 text-xs">
        <input type="radio" id="inactive" v-model="searchParams.active" value="inactive">
        <label for="inactive">Inactive</label>
      </div>

      <div class="uppercase inline-block mr-2 md:mr-5 lg:mr-3 xl:mr-3 text-xs">
        <input type="radio" id="deleted" v-model="searchParams.active" value="deleted">
        <label for="deleted">Deleted</label>
      </div>
      <div class="uppercase inline-block mr-2 md:mr-5 lg:mr-3 xl:mr-3 text-xs" v-if="selectedOffice.network_id !== 8">
        <input type="radio" id="rejected" v-model="searchParams.active" value="rejected">
        <label for="rejected">Rejected</label>
      </div>
    </div>
    <div class="w-full flex flex-wrap mt-4" v-if="!loading && uids.length > 0 && (uids.includes('rightmove') || uids.includes('rightmove_overseas') || uids.includes('zoopa'))">
      <div class="flex flex-col w-1/3 pr-2 mb-4 justify-end">
        <label for="feed_flag" class="text-xs uppercase mb-1">Portals:</label>
        <pl-select
          v-model="searchParams.feed_flag"
          name="feed_flag"
          id="feed_flag"
          select-class="bg-white rounded h-10 py-0 shadow text-sm"
        >
          <option value="">Both</option>
          <option value="rightmove_url">Rightmove</option>
          <option value="rightmove_overseas_url">Rightmove Overseas</option>
          <option value="zoopla_url">Zoopla</option>
        </pl-select>
      </div>
    </div>
    <panel class="mt-5 pt-6" v-if="results.data && results.data.length > 0">
      <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
        <table class="table-auto text-center w-full">
          <thead class="uppercase text-xs">
          <tr>
            <th class="text-left p-2 whitespace-no-wrap cursor-pointer" colspan="2"
                @click="changeOrder('display_address')">
              Property
              <i class="fas fa-caret-down"
                 :class="{'fa-caret-down':this.searchParams.order_direction === 'desc', 'fa-caret-up':this.searchParams.order_direction === 'asc'}"
                 v-if="this.searchParams.order_attribute === 'display_address'"/>
            </th>
            <th class="p-2 whitespace-no-wrap cursor-pointer" @click="changeOrder('price')">
              Price
              <i class="fas fa-caret-down"
                 :class="{'fa-caret-down':this.searchParams.order_direction === 'desc', 'fa-caret-up':this.searchParams.order_direction === 'asc'}"
                 v-if="this.searchParams.order_attribute === 'price'"/>
            </th>
            <th class="p-2 whitespace-no-wrap cursor-pointer" @click="changeOrder('images_count')">
              Images
              <i class="fas fa-caret-down"
                 :class="{'fa-caret-down':this.searchParams.order_direction === 'desc', 'fa-caret-up':this.searchParams.order_direction === 'asc'}"
                 v-if="this.searchParams.order_attribute === 'images_count'"/>
            </th>
            <th class="p-2 whitespace-no-wrap cursor-pointer" @click="changeOrder('status_id')">
              Availability
              <i class="fas fa-caret-down"
                 :class="{'fa-caret-down':this.searchParams.order_direction === 'desc', 'fa-caret-up':this.searchParams.order_direction === 'asc'}"
                 v-if="this.searchParams.order_attribute === 'status_id'"/>
            </th>
            <th class="p-2 whitespace-no-wrap cursor-pointer" @click="changeOrder('created_at')">
              Uploaded
              <i class="fas fa-caret-down"
                 :class="{'fa-caret-down':this.searchParams.order_direction === 'desc', 'fa-caret-up':this.searchParams.order_direction === 'asc'}"
                 v-if="this.searchParams.order_attribute === 'created_at'"/>
            </th>
            <th class="p-2 whitespace-no-wrap cursor-pointer" @click="changeOrder('updated_at')">
              Updated
              <i class="fas fa-caret-down"
                 :class="{'fa-caret-down':this.searchParams.order_direction === 'desc', 'fa-caret-up':this.searchParams.order_direction === 'asc'}"
                 v-if="this.searchParams.order_attribute === 'updated_at'"/>
            </th>
            <th class="p-2">Status</th>
            <th class="p-2" v-if="uids.length > 0 && (uids.includes('rightmove') || uids.includes('rightmove_overseas') || uids.includes('zoopa'))">Portals</th>
          </tr>
          </thead>
          <tbody class="text-sm">
          <tr v-for="(property, i) in results.data" :key="i"
              class="border-t border-grey-lighter hover:bg-grey-lightest cursor-pointer"
              >
            <td>
              <router-link :to="`property-management/${property.id}`" class="no-underline text-inherit"
                           @click.native="goToProperty(property.id)"
              >
                <div class="icon-img w-24 h-16 my-2 rounded"
                     :style="{'background-image': `url('${property.main_photo}')`}" v-if="property.main_photo"></div>
                <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                  <icon name="property_cms" class="text-white"/>
                </div>
              </router-link>
            </td>
            <td class="text-left p-2 font-bold">
              <router-link :to="`property-management/${property.id}`" @click.native="goToProperty(property.id)" class="block w-full h-full no-underline text-inherit">{{ property.short_address }}</router-link></td>
            <td class="p-2"><router-link :to="`property-management/${property.id}`" @click.native="goToProperty(property.id)" class="block w-full h-full no-underline text-inherit">{{ property.price }}</router-link></td>
            <td class="p-2"><router-link :to="`property-management/${property.id}`" @click.native="goToProperty(property.id)" class="block w-full h-full no-underline text-inherit">{{ property.media.images }}</router-link></td>
            <td class="p-2"><router-link :to="`property-management/${property.id}`" @click.native="goToProperty(property.id)" class="block w-full h-full no-underline text-inherit">{{ property.availability.name }}</router-link></td>
            <td class="p-2"><router-link :to="`property-management/${property.id}`" @click.native="goToProperty(property.id)" class="block w-full h-full no-underline text-inherit">{{ property.uploaded_at }}</router-link></td>
            <td class="p-2"><router-link :to="`property-management/${property.id}`" @click.native="goToProperty(property.id)" class="block w-full h-full no-underline text-inherit">{{ property.updated_at }}</router-link></td>
            <td class="p-2">
              <router-link :to="`property-management/${property.id}`" @click.native="goToProperty(property.id)" class="block w-full h-full no-underline text-inherit">
                <i class="fa fa-check-circle fa-2x text-green" v-if="property.status.id === 0" title="Active Property"/>
                <i class="fa fa-trash-alt fa-2x" v-if="property.status.id === 1" title="Deleted Property"/>
                <i class="fa fa-times-circle fa-2x" v-if="property.status.id === 2" title="Inactive Property"/>
                <i class="fas fa-exclamation-triangle fa-2x text-red" v-if="property.status.id === 3" title="Rejected Property"/>
              </router-link>
            </td>
            <td class="p-2 w-24" v-if="uids.length > 0 && (uids.includes('rightmove') || uids.includes('rightmove_overseas') || uids.includes('zoopa'))">
              <a :href="property.zoopla_url"
              target="_blank"
              v-if="property.zoopla_url"
              :class="{'mb-4':property.zoopla_url}">
                  <img :src="`/feed_provider_logos/zoopla.png`" class="w-12" />
              </a>
              <br class="mb-2" v-if="property.zoopla_url && (property.rightmove_url || property.rightmove_overseas_url)">
              <a :href="property.rightmove_url" target="_blank"
              v-if="property.rightmove_url">
                  <img :src="`/feed_provider_logos/rightmove.png`" />
              </a>
              <a :href="property.rightmove_overseas_url" target="_blank"
              v-if="property.rightmove_overseas_url">
                  <img :src="`/feed_provider_logos/rightmove.png`" />
              </a>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <paginator :data="results.meta" @change="pageChanged"/>
    </panel>

    <panel v-if="!loading && results.data && results.data.length === 0" class="mt-5 pt-6">
      <strong class="mb-2">No Properties</strong>
      <p>Your search returned no results</p>
    </panel>
    <div v-if="loading" class="w-full h-screen text-center">
      <loader></loader>
    </div>

    <modal v-if="magazineAlert">
      <div class="p-2">
        {{ magazineAlert }}
      </div>
      <button @click="magazineAlert = null"
              class="bg-tertiary border border-tertiary text-white rounded px-4 py-3 text-sm font-medium align-bottom m-2">
        OK
      </button>
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import api from '@/http'
import debounce from 'lodash/debounce'

export default {
  name: 'PropertyManagement',

  data () {
    return {
      searchParams: {},
      meta: {},
      results: {},
      creatingProperty: false,
      loading: true,
      loadingEhouse: false,
      loadingDigitalHub: false,
      magazineAlert: null,
      pageHasChanged: false,
      loadingStats: false,
      uids: []
    }
  },
  watch: {
    searchParams: {
      handler: function (newVal, oldVal) {
        this.search()
      },
      deep: true
    },
    system () {
      this.searchParams.system_id = this.system.id

      if (this.selectedOffice) {
        this.search()
      }
    },
    selectedOffice: {
      handler: function () {
        this.searchParams.office_id = this.selectedOffice.id

        if (this.system) {
          this.search()
        }
        this.getFeedsUid()
      },
      deep: true
    }
  },
  computed: {
    ...mapState(['activeProperties', 'system', 'selectedOffice'])
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading']),
    changeOrder (attribute) {
      this.searchParams.page = 1

      if (attribute === this.searchParams.order_attribute) {
        this.searchParams.order_direction = this.searchParams.order_direction === 'desc' ? 'asc' : 'desc'
        return
      }

      this.searchParams.order_attribute = attribute
      this.searchParams.order_direction = 'desc'
    },
    getMeta: function () {
      return api.get('/properties/meta', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.meta = response.data
      })
    },
    search: debounce(function () {
      if (!this.searchParams.office_id) {
        return
      }

      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      api.get('/properties', {
        params: this.searchParams
      }).then((response) => {
        this.results = response.data
        this.setPageLoading(false)
        this.loading = false
      }, () => {
        // Error
        this.loading = false
        this.setPageLoading(false)
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    }, 500),
    goToProperty (id) {
      this.$router.push(`/property-management/${id}`)
    },
    newProperty (department) {
      this.creatingProperty = true
      api.post(`/properties`, {
        department: department,
        office_id: this.selectedOffice.id
      }).then((response) => {
        this.$router.push(`/property-management/${response.data.id}`)
      }, () => {
        this.creatingProperty = false
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    },
    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
    },
    createMagazinePage () {
      if (this.loadingDigitalHub) {
        return
      }

      this.loadingDigitalHub = true
      let newTab = window.open()

      api.post('/digital-hub/market-me-now', {
        office_id: this.selectedOffice.id,
        redirect_url: `/order/publication/select/type`
      }).then((response) => {
        this.loadingDigitalHub = false
        newTab.location.href = response.data.link
      }, () => {
        newTab.close()
      })
    },
    goToEhouse () {
      if (this.loadingEhouse) {
        return
      }

      this.loadingEhouse = true
      let newTab = window.open()
      api.get('ehouse', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.loadingEhouse = false
        newTab.location.href = response.data.url
      }, (error) => {
        newTab.close()
        this.loadingEhouse = false
        if (error.response.status === 422) {
          this.displayToastNotification({
            type: 'warning',
            icon: 'fa-exclamation-triangle',
            title: 'Error',
            info: error.response.data.message
          })

          return
        }

        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    },
    getFeedsUid: function () {
      return api.get('/properties/feeds-uid', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.uids = response.data
      })
    }
  },
  created () {
    this.getMeta()
    this.getFeedsUid()
    this.searchParams = {
      system_id: this.system.id,
      office_id: this.selectedOffice.id,
      q: '',
      department: 'RS',
      active: 'active',
      order_attribute: 'price',
      order_direction: 'desc',
      feed_flag: '',
      page: 1
    }
  }
}
</script>
<style scoped>
  .icon-img {
    background-size: cover;
    background-position: center;
  }

  .icon {
    width: 80px;
    height: 60px;
  }

  .text-inherit {
    color: inherit;
    text-decoration: none;
  }
</style>
